/**
 * Bookshelf — the canonical resting place for memory objects.
 *
 * Used by:
 *   • /event/:slug/archive (the full destination)
 *   • EventPage organiser dashboard widget (a quiet preview)
 *
 * Visual rules per Craig's brief:
 *   • Wooden shelf surface (matches the Memory Wall — same family)
 *   • Spines NOT perfectly aligned (each one carries server-deterministic
 *     lean/height/thickness/cloth so the shelf feels curated over time)
 *   • Quiet ambient breathing — slow box-shadow respiration, NO bouncing
 *   • Subtle dust motes drifting across the shelf — three CSS-animated
 *     specks at low opacity; they cross every ~14s and never collide
 *   • A spine slides into place on first paint (staggered entrance)
 *   • Empty state is dignified — "The beginning of an archive ✦",
 *     NO illustration, NO "0 chapters yet"
 *
 * Anti-patterns (HARD locks):
 *   • NO checkboxes, kebab menus, sort dropdowns
 *   • NO "created at" / "last updated" timestamps shown anywhere
 *   • NO file extensions, NO download counts, NO pagination
 *   • Interaction copy: "revisit" / "pull from the shelf" — never "Open"
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ChapterSpine from './ChapterSpine';
import RecapLetter from './RecapLetter';
import PosterSpine from './PosterSpine';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Wood-grain shelf surface — same family as the Memory Wall but
// quieter and lower-contrast since this is read NOT performed against.
const SHELF_WOOD = `
  linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0) 25%, rgba(0,0,0,0) 75%, rgba(0,0,0,0.55) 100%),
  repeating-linear-gradient(
    90deg,
    rgba(40,22,8,0.0) 0px,
    rgba(40,22,8,0.0) 60px,
    rgba(40,22,8,0.45) 62px,
    rgba(40,22,8,0.45) 64px
  ),
  linear-gradient(180deg, #3a2615 0%, #2a1a0c 50%, #1e1209 100%)
`;

const SHELF_LIP = `
  linear-gradient(180deg, #4a3018 0%, #2a1a0c 100%)
`;

export default function Bookshelf({
  eventId,
  slug,
  variant = 'full',          // 'full' (archive route) | 'compact' (dashboard widget)
  shelfHeight,
  showDust = true,
  onPullChapter,
  onPullLetter,
  onPullPoster,
  data,                       // optional pre-fetched archive payload
  guestId = null,             // forwarded into pull handlers if needed
  focusId = null,             // optional id of an object to glow on entrance
}) {
  const [archive, setArchive] = useState(data || null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (data) { setArchive(data); setLoading(false); return; }
    if (!eventId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/${eventId}/archive`);
        if (!cancelled) setArchive(r.data);
      } catch {
        if (!cancelled) setArchive({ objects: [], object_count: 0 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [eventId, data]);

  const isCompact = variant === 'compact';
  const baseHeight = shelfHeight || (isCompact ? 200 : 360);
  // Mobile-aware: the dashboard widget needs to be thumb-friendly on phones
  // but the archive page benefits from generous wall-space.
  const surfacePadding = isCompact ? '34px 26px 22px' : '64px 64px 42px';
  const objects = archive?.objects || [];
  const isEmpty = !loading && objects.length === 0;

  // We layer the shelf so it FEELS like a real piece of furniture:
  //   • back panel (dark wall behind shelf)
  //   • shelf surface (where books rest)
  //   • shelf lip / front edge
  // No 3D, no perspective — just careful flat composition.
  return (
    <div
      data-testid="bookshelf-surface"
      style={{
        position: 'relative',
        width: '100%',
        background: SHELF_WOOD,
        padding: surfacePadding,
        borderRadius: isCompact ? 16 : 22,
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,235,210,0.06)',
        minHeight: baseHeight + (isCompact ? 60 : 120),
      }}
    >
      {/* Ambient golden glow — like warm room light hitting the shelf */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(ellipse at 50% 25%, rgba(212,175,94,0.10) 0%, transparent 60%)',
        pointerEvents: 'none',
      }} />

      {/* Dust motes — three slow drifting specks for ambient warmth */}
      {showDust && !isCompact && (
        <>
          <div aria-hidden style={{
            position: 'absolute', width: 3, height: 3, borderRadius: '50%',
            background: 'rgba(255,230,180,0.32)', filter: 'blur(0.5px)',
            animation: 'shelfDust1 18s linear infinite',
            top: '20%', left: '-4%',
          }} />
          <div aria-hidden style={{
            position: 'absolute', width: 2, height: 2, borderRadius: '50%',
            background: 'rgba(255,230,180,0.26)', filter: 'blur(0.4px)',
            animation: 'shelfDust2 24s linear infinite',
            top: '55%', left: '-4%',
            animationDelay: '6s',
          }} />
          <div aria-hidden style={{
            position: 'absolute', width: 2.5, height: 2.5, borderRadius: '50%',
            background: 'rgba(255,230,180,0.30)', filter: 'blur(0.5px)',
            animation: 'shelfDust3 22s linear infinite',
            top: '35%', left: '-4%',
            animationDelay: '12s',
          }} />
        </>
      )}

      {/* SHELF SURFACE — books rest along a flat baseline */}
      <div
        data-testid="bookshelf-objects"
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: isEmpty ? 'center' : 'flex-start',
          gap: isCompact ? 6 : 8,
          minHeight: baseHeight,
          flexWrap: 'nowrap',
          overflowX: isCompact ? 'auto' : 'visible',
          overflowY: 'visible',
          paddingBottom: 4,
          // Subtle baseline shadow under the books — feels grounded
          background: 'radial-gradient(ellipse at 50% 100%, rgba(0,0,0,0.55) 0%, transparent 60%)',
        }}
      >
        {loading && (
          <div style={{
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            color: 'rgba(212,175,94,0.65)', fontSize: 13, letterSpacing: '0.03em',
          }}>
            settling on the shelf ✦
          </div>
        )}

        {isEmpty && (
          <div data-testid="bookshelf-empty" style={{
            textAlign: 'center', maxWidth: 460, padding: '40px 24px',
          }}>
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
              color: 'rgba(212,175,94,0.78)', marginBottom: 14,
            }}>
              The beginning of an archive
            </div>
            <div style={{
              fontFamily: 'Georgia, serif',
              fontSize: isCompact ? 17 : 22,
              color: '#FFE7A8', letterSpacing: '-0.01em', lineHeight: 1.3,
              marginBottom: 12,
              textShadow: '0 0 18px rgba(212,175,94,0.20)',
            }}>
              A place is held for the chapters from this night ✦
            </div>
            <div style={{
              fontFamily: 'Georgia, serif', fontStyle: 'italic',
              fontSize: 13, color: 'rgba(255,235,210,0.55)',
              lineHeight: 1.55,
            }}>
              When the first chapter is composed, it will rest here.
            </div>
          </div>
        )}

        {!loading && !isEmpty && objects.map((obj, i) => {
          const delay = i * 140;   // staggered entrance — feels curated
          const focused = focusId && obj.id === focusId;
          if (obj.type === 'chapter') {
            return (
              <ChapterSpine
                key={obj.id}
                chapter={obj}
                shelfHeight={baseHeight}
                entranceDelay={delay}
                focused={focused}
                onPull={onPullChapter}
              />
            );
          }
          if (obj.type === 'letter') {
            return (
              <RecapLetter
                key={obj.id}
                letter={obj}
                shelfHeight={baseHeight}
                entranceDelay={delay}
                focused={focused}
                onPull={(l) => onPullLetter && onPullLetter(l, guestId)}
              />
            );
          }
          if (obj.type === 'poster') {
            return (
              <PosterSpine
                key={obj.id}
                poster={obj}
                shelfHeight={baseHeight}
                entranceDelay={delay}
                focused={focused}
                onPull={onPullPoster}
              />
            );
          }
          return null;
        })}
      </div>

      {/* Shelf front edge — physical lip the books rest against */}
      <div aria-hidden style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        height: isCompact ? 12 : 18,
        background: SHELF_LIP,
        boxShadow: '0 -2px 8px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,235,210,0.05)',
      }} />

      {/* Keyframes — kept inline so the component is self-contained */}
      <style>{`
        @keyframes spineEnter {
          0%   { opacity: 0; transform: translateY(40px) rotate(0deg); }
          70%  { opacity: 1; }
          100% { opacity: 1; }
        }
        @keyframes spineBreathe {
          0%, 100% { filter: brightness(1.0); }
          50%      { filter: brightness(1.04); }
        }
        @keyframes letterBreathe {
          0%, 100% { filter: brightness(1.0); }
          50%      { filter: brightness(1.05); }
        }
        @keyframes spineFocusGlow {
          0%   { filter: brightness(1.0); }
          50%  { filter: brightness(1.18); }
          100% { filter: brightness(1.0); }
        }
        @keyframes shelfDust1 {
          0%   { transform: translate(0vw, 0) scale(0.8); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(110vw, -40px) scale(1.1); opacity: 0; }
        }
        @keyframes shelfDust2 {
          0%   { transform: translate(0vw, 0) scale(0.7); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(110vw, -25px) scale(1.0); opacity: 0; }
        }
        @keyframes shelfDust3 {
          0%   { transform: translate(0vw, 0) scale(0.9); opacity: 0; }
          15%  { opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translate(110vw, -55px) scale(1.2); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
